import React from 'react';
import {
  Button,
  ButtonType,
  Row
} from 'syngenta-digital-cropwise-react-ui-kit';
import FormSection from 'components/FormSection';
import EditOutlinedIcon from 'assets/edit_outlined.svg?react';
import formatNumber from 'helpers/formatNumber';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { CropZoneRecord } from '../types';
import useTotalAcreageEditor from './useTotalAcreageEditor';
import EditTotalAcreageModal from './EditTotalAcreageModal';

export interface TotalAcreageSummaryProps<T extends CropZoneRecord> {
  totalAppliedAcreage: number;
  cropZones: T[] | undefined;
  setFieldValue: (field: string, value: unknown) => void;
  onTotalAcreageChange: (total: number) => void;
  addButton: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The Properties step's "Total Applied Acreage" summary header, its edit
 * button, and the Edit Total Applied Acreage modal it opens — identical
 * between Applications/Work Orders and Plans. Renders the wrapped table
 * (`children`) inside the same `FormSection`; each screen supplies its own
 * "select properties" button via `addButton` since the label/testid differ.
 */
const TotalAcreageSummary = <T extends CropZoneRecord>({
  totalAppliedAcreage,
  cropZones,
  setFieldValue,
  onTotalAcreageChange,
  addButton,
  children
}: TotalAcreageSummaryProps<T>): React.ReactElement => {
  const {
    isEditModalOpen,
    totalAcresDisplay,
    totalCoveragePercent,
    handleOpenEditModal,
    handleCloseEditModal,
    handleSaveEditModal,
    handleTotalAcresBlur,
    handleTotalAcresChange,
    handleTotalAcresFocus,
    handleTotalCoveragePercentBlur,
    handleTotalCoveragePercentChange,
    handleTotalCoveragePercentFocus
  } = useTotalAcreageEditor(
    cropZones,
    totalAppliedAcreage,
    setFieldValue,
    onTotalAcreageChange
  );

  return (
    <div className="flex flex-col w-full">
      <FormSection
        isOptional
        hasWrapper={false}
        autoHeight
        headerContent={
          <div className="mr-8">
            <Row
              prefixCls="syt-antd-row"
              className="justify-end text-neutral-60 leading-4 mb-6px"
            >
              {LayoutStrings.cropZones.totalAppliedAcreage}
            </Row>
            <Row prefixCls="syt-antd-row" className="justify-end text-base">
              {formatNumber(totalAppliedAcreage)}{' '}
              {LayoutStrings.cropZones.areaUnit}
              <Button
                type={ButtonType.outline}
                size="small"
                icon={<EditOutlinedIcon />}
                className="ml-2"
                style={{ padding: '2px' }}
                onClick={handleOpenEditModal}
              />
            </Row>
          </div>
        }
        button={addButton}
      >
        {children}
      </FormSection>

      <EditTotalAcreageModal
        open={isEditModalOpen}
        totalAcresDisplay={totalAcresDisplay}
        totalCoveragePercent={totalCoveragePercent}
        onCancel={handleCloseEditModal}
        onSave={handleSaveEditModal}
        onTotalAcresChange={handleTotalAcresChange}
        onTotalAcresFocus={handleTotalAcresFocus}
        onTotalAcresBlur={handleTotalAcresBlur}
        onTotalCoveragePercentChange={handleTotalCoveragePercentChange}
        onTotalCoveragePercentFocus={handleTotalCoveragePercentFocus}
        onTotalCoveragePercentBlur={handleTotalCoveragePercentBlur}
      />
    </div>
  );
};

export default TotalAcreageSummary;
